import { ApiChartAttribute, ApiMetric } from "@incendium/api";
import {
  buildProductTrendCacheKey,
  fetchProductTrendPoints,
} from "features/products/utils/productTrend";
import { useSelectedProject } from "Hooks";
import { IFromToChartOutput, useFromToContext } from "Providers/FromToProvider";
import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
} from "react";

export interface ProductTrendSnapshot {
  points: number[];
  loading: boolean;
}

interface ProductTrendContextValue {
  requestTrend: (productName: string, trendMetric: ApiMetric) => void;
  subscribe: (key: string, listener: () => void) => () => void;
  getSnapshot: (key: string) => ProductTrendSnapshot;
  getCacheKey: (productName: string, trendMetric: ApiMetric) => string;
}

const EMPTY_SNAPSHOT: ProductTrendSnapshot = { points: [], loading: true };

const ProductTrendContext = createContext<ProductTrendContextValue | null>(null);

function buildScopeKey(
  projectId?: number,
  chartAttributes?: ApiChartAttribute[],
  chartOutput?: IFromToChartOutput | null,
) {
  return buildProductTrendCacheKey({
    projectId,
    productName: "__scope__",
    trendMetric: ApiMetric.METRIC_NOT_SET,
    chartAttributes: chartAttributes || [],
    chartOutput: chartOutput || null,
  });
}

export function ProductTrendProvider({
  chartAttributes,
  children,
}: {
  chartAttributes: ApiChartAttribute[];
  children: ReactNode;
}) {
  const { selectedProject } = useSelectedProject();
  const { chartOutput } = useFromToContext();
  const storeRef = useRef({
    cache: new Map<string, ProductTrendSnapshot>(),
    listeners: new Map<string, Set<() => void>>(),
    inflight: new Map<string, Promise<void>>(),
  });
  const scopeRef = useRef("");

  const scopeKey = useMemo(
    () => buildScopeKey(selectedProject?.id, chartAttributes, chartOutput),
    [chartAttributes, chartOutput, selectedProject?.id],
  );

  useEffect(() => {
    if (scopeRef.current === scopeKey) {
      return;
    }

    scopeRef.current = scopeKey;
    const store = storeRef.current;
    store.cache.clear();
    store.inflight.clear();
    store.listeners.forEach((listeners) => listeners.forEach((listener) => listener()));
  }, [scopeKey]);

  const emit = useCallback((key: string) => {
    storeRef.current.listeners.get(key)?.forEach((listener) => listener());
  }, []);

  const getCacheKey = useCallback(
    (productName: string, trendMetric: ApiMetric) =>
      buildProductTrendCacheKey({
        projectId: selectedProject?.id,
        productName,
        trendMetric,
        chartAttributes,
        chartOutput,
      }),
    [chartAttributes, chartOutput, selectedProject?.id],
  );

  const requestTrend = useCallback(
    (productName: string, trendMetric: ApiMetric) => {
      if (!selectedProject?.id || !productName || !chartOutput) {
        return;
      }

      const key = getCacheKey(productName, trendMetric);
      const store = storeRef.current;
      const existing = store.cache.get(key);

      if (existing && !existing.loading) {
        return;
      }

      if (store.inflight.has(key)) {
        return;
      }

      store.cache.set(key, {
        points: existing?.points ?? [],
        loading: true,
      });
      emit(key);

      const promise = fetchProductTrendPoints({
        projectId: selectedProject.id,
        productName,
        trendMetric,
        chartAttributes,
        chartOutput,
      })
        .then((points) => {
          store.cache.set(key, { points, loading: false });
          store.inflight.delete(key);
          emit(key);
        })
        .catch(() => {
          store.cache.set(key, { points: [], loading: false });
          store.inflight.delete(key);
          emit(key);
        });

      store.inflight.set(key, promise);
    },
    [chartAttributes, chartOutput, emit, getCacheKey, selectedProject?.id],
  );

  const subscribe = useCallback((key: string, listener: () => void) => {
    const store = storeRef.current;
    if (!store.listeners.has(key)) {
      store.listeners.set(key, new Set());
    }

    store.listeners.get(key)!.add(listener);
    return () => {
      store.listeners.get(key)?.delete(listener);
    };
  }, []);

  const getSnapshot = useCallback((key: string) => {
    return storeRef.current.cache.get(key) ?? EMPTY_SNAPSHOT;
  }, []);

  const value = useMemo(
    () => ({
      requestTrend,
      subscribe,
      getSnapshot,
      getCacheKey,
    }),
    [getCacheKey, getSnapshot, requestTrend, subscribe],
  );

  return (
    <ProductTrendContext.Provider value={value}>
      {children}
    </ProductTrendContext.Provider>
  );
}

export function useProductTrendContext() {
  const context = useContext(ProductTrendContext);
  if (!context) {
    throw new Error("useProductTrendContext must be used within ProductTrendProvider");
  }

  return context;
}
